import { useState, useEffect } from "react";
import { format, differenceInDays } from "date-fns";
import BookingGetters from "../../../store/onlineBooking/getters";
import {
  BookingVehicleInfoCardProps,
  ChosenOptions,
} from "./BookingVehicleInfoCard.props";
import { useTranslation } from "react-i18next";

const useVehicleInfo = (VehicleDetails: BookingVehicleInfoCardProps) => {
  const ReservationDetails = BookingGetters.getBookingDetails();
  const appSettings = BookingGetters.getAppSettings();

  // Image handling
  const [selectedImage, setSelectedImage] = useState(0);
  const totalImages = VehicleDetails.vehicleImages.length;

  const getNewImageIndex = (
    current: number,
    total: number,
    direction: number
  ) => (current + direction + total) % total;

  const handleBackClick = () =>
    setSelectedImage((prev) => getNewImageIndex(prev, totalImages, -1));

  const handleForwardClick = () =>
    setSelectedImage((prev) => getNewImageIndex(prev, totalImages, 1));

  // Map settings
  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
  };

  // State management
  const [currentIndex, setCurrentIndex] = useState(3);
  const [latitude] = useState(VehicleDetails.latitude);
  const [longitude] = useState(VehicleDetails.longitude);
  const [selectedFields] = useState<ChosenOptions>({
    insurance: undefined,
    options: {},
  });

  // Screen size detection
  const istabletScreen = window.innerWidth >= 768 && window.innerWidth <= 1220;
  const isSmallScreen = window.innerWidth <= 768;
  const isIphoneScreen = window.innerWidth >= 400 && window.innerWidth <= 768;
  const isAndroidScreen = window.innerWidth <= 400;
  const [isDesktop] = useState(window.innerWidth >= 1310);

  // Date calculations
  const [startDate] = useState(
    new Date(
      format(
        new Date(ReservationDetails.pickUpDateAndTime || Date.now()),
        "yyyy-MM-dd"
      )
    )
  );

  const [endDate] = useState(
    new Date(
      format(
        new Date(ReservationDetails.dropOffDateAndTime || Date.now()),
        "yyyy-MM-dd"
      )
    )
  );

  const [numberOfDays] = useState(differenceInDays(endDate, startDate));

  // Price calculations
  const [pricePerDay] = useState(
    parseFloat(VehicleDetails.priceDetails.payNow.toString()) /
      (numberOfDays == 0 ? 1 : numberOfDays)
  );

  const [totalPricePerDay, setTotalPricePerDay] = useState(pricePerDay);
  const [discount, setDiscount] = useState(
    (100 - VehicleDetails.discount) / 100
  );
  const [totalPrice, setTotalPrice] = useState<number>(
    parseFloat(VehicleDetails.priceDetails.payNow) * discount
  );

  const [paymentType] = useState<"PAYNOW" | "PAYLATER" | undefined>();
  const payNowDisAmount =
    (parseFloat(VehicleDetails.priceDetails.payLater.toString()) *
      VehicleDetails.discount) /
    100;

  // Vehicle parameters
  const [vehicleParameters, setVehicleParameters] = useState([]);

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showTechnicalSheet, setShowTechnicalSheet] = useState(false);

  // Map container style
  const mapContainerStyle = {
    width: !isSmallScreen ? "100%" : "90%",
    height: !isSmallScreen ? "300px" : "230px",
    backgroundColor: "#f0f0f0",
    borderRadius: !isSmallScreen ? "7px" : "15px",
    overflow: "hidden",
    marginLeft: !isSmallScreen ? "" : "20px",
  };

  // Effects
  useEffect(() => {
    setDiscount((100 - VehicleDetails.discount) / 100);
  }, [VehicleDetails.discount]);

  useEffect(() => {
    let price: number = parseFloat(
      selectedFields.insurance?.price?.toString() || "0"
    );
    if (selectedFields.options) {
      Object.values(selectedFields.options).forEach((el) => {
        price += el.price * el.quantity;
      });
    }
    const totalPrice =
      parseFloat(VehicleDetails.priceDetails.payNow) * discount;
    const totalPriceAfterExtra = price + totalPrice;
    setTotalPricePerDay(
      totalPriceAfterExtra / (numberOfDays == 0 ? 1 : numberOfDays)
    );
    setTotalPrice(totalPriceAfterExtra);
  }, [selectedFields]);

  useEffect(() => {
    setTotalPrice(parseFloat(VehicleDetails.priceDetails.payNow) * discount);
    setTotalPricePerDay(
      parseFloat(
        (parseFloat(VehicleDetails.priceDetails.payNow) * discount).toString()
      ) / (numberOfDays == 0 ? 1 : numberOfDays)
    );
  }, [VehicleDetails.priceDetails]);

  useEffect(() => {
    const sortedParameters = [...VehicleDetails.specifications].sort((a, b) => {
      if (a.value != b.value) return a.value - b.value;
      return a.name.localeCompare(b.name);
    });
    setVehicleParameters(sortedParameters);
  }, [VehicleDetails.specifications]);

  return {
    vehicleParameters,
    selectedImage,
    totalImages,
    handleBackClick,
    handleForwardClick,
    latitude,
    longitude,
    istabletScreen,
    translate: useTranslation().t,
    startDate,
    endDate,
    numberOfDays,
    pricePerDay,
    totalPricePerDay,
    setTotalPricePerDay,
    discount,
    totalPrice,
    setTotalPrice,
    paymentType,
    payNowDisAmount,
    isSmallScreen,
    isIphoneScreen,
    isAndroidScreen,
    isDesktop,
    openModal,
    setOpenModal,
    isModalOpen,
    setIsModalOpen,
    showMap,
    setShowMap,
    showTechnicalSheet,
    setShowTechnicalSheet,
    mapContainerStyle,
    mapOptions,
    appSettings,
    selectedFields,
    currentIndex,
    setCurrentIndex,
    setSelectedImage,
  };
};

export default useVehicleInfo;
